/**
 * Batch sync engine for the offline punch queue (API-4379).
 *
 * Reads the whole IndexedDB queue and uploads it as a single all-or-nothing
 * batch whenever connectivity/auth conditions make that plausible, exposing
 * the resulting `SyncState` via `useSyncState()` for the offline UX
 * (API-4380). The engine is deliberately token-agnostic: it uses whatever
 * `AuthContext.accessToken` is current and treats any failure (401 or
 * otherwise) as "retain the queue, retry on the next trigger" — no data is
 * ever lost, since the queue itself lives in IndexedDB independent of this
 * context. See `offline-token-lifecycle-gaps-plan` for why an
 * access-token-changed trigger exists alongside reconnect.
 *
 * Triggers:
 *   - mount / account available — fires once, either at provider mount (if
 *     the account is already resolved) or the first time `account` transitions
 *     from absent to present. `account` resolves asynchronously (after the
 *     access token), so this can't be a mount-only effect — see the trigger's
 *     own comment for why.
 *   - reconnect    — `isOnline` false -> true (a *confirmed* reconnection,
 *     per `NetworkStatusContext`).
 *   - token change — a new non-null `accessToken` arrives while online; the
 *     reconnect-recovery path for a stale in-memory token.
 *   - went offline — `isOnline` true -> false; moves to `unavailable`.
 *   - punch added  — the queue changed (see `offlinePunchQueue`); keeps the
 *     displayed count live without polling.
 */

import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@src/auth/AuthContext';
import { useTerminalAccount } from '@src/context/AccountContext';
import { useNetworkStatus } from '@src/context/NetworkStatusContext';
import {
    getAllPunches,
    getCount,
    clearPunches,
    subscribeQueueChanged
} from '@src/config/offlinePunchQueue';
import { syncOfflinePunches } from '@src/api/syncOfflineApi';
import { setInFlightSync } from '@src/config/syncCoordinator';
import type { SyncState } from '@src/config/syncStateMachine';
import {
    nothingToSync,
    readyToSync,
    syncing,
    synced,
    unavailable
} from '@src/config/syncStateMachine';

// How long the transient `synced` status stays visible before resetting to
// `nothingToSync`. Kept as a context-level constant (not in the pure state
// machine) so the timing stays easy to test/adjust.
const SYNCED_RESET_DELAY_MS = 1500;

const SyncStateContext = createContext<SyncState | null>(null);

export function SyncStateProvider({ children }: { children: ReactNode }) {
    const { account } = useTerminalAccount();
    const { accessToken } = useAuth();
    const { isOnline } = useNetworkStatus();

    const [state, setState] = useState<SyncState>(nothingToSync());

    const accountRef = useRef(account);
    accountRef.current = account;
    const accessTokenRef = useRef(accessToken);
    accessTokenRef.current = accessToken;
    const isOnlineRef = useRef(isOnline);
    isOnlineRef.current = isOnline;

    const isSyncingRef = useRef(false);
    const isMountedRef = useRef(true);
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevOnlineRef = useRef(isOnline);
    const prevTokenRef = useRef(accessToken);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        };
    }, []);

    function setStateIfMounted(next: SyncState) {
        if (isMountedRef.current) setState(next);
    }

    async function runSync(): Promise<void> {
        const currentAccount = accountRef.current;
        const currentToken = accessTokenRef.current;

        if (
            !currentAccount ||
            !currentToken ||
            !isOnlineRef.current ||
            isSyncingRef.current
        ) {
            return;
        }

        const punches = await getAllPunches(currentAccount.companyId);
        if (punches.length === 0) {
            setStateIfMounted(nothingToSync());
            return;
        }

        const count = punches.length;
        isSyncingRef.current = true;
        setStateIfMounted(syncing(count));

        const syncPromise = syncOfflinePunches(
            currentAccount.companyId,
            punches,
            currentToken
        );
        setInFlightSync(syncPromise);

        try {
            await syncPromise;
            // Clear only the ids in the uploaded snapshot — punches may have
            // been queued for this company after `punches` was read but
            // before the batch finished uploading, and those must survive.
            const syncedIds = punches
                .map(punch => punch.id)
                .filter((id): id is number => id != null);
            await clearPunches(syncedIds);
            setStateIfMounted(synced());
            if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
            resetTimerRef.current = setTimeout(() => {
                // Recompute the live state instead of unconditionally
                // resetting to `nothingToSync` — punches may have been
                // queued again, or the terminal may have gone offline,
                // during the transient `synced` display window, and a blind
                // reset would clobber that newer, more accurate state.
                void refreshLiveState();
            }, SYNCED_RESET_DELAY_MS);
        } catch (err) {
            console.error('[SyncStateContext] Offline punch sync failed', err);
            // Re-read the queue rather than reusing the uploaded batch size:
            // punches may have been queued for this company while the failed
            // batch was in flight, so the original `count` can under-report the
            // true unsynced total. Fall back to `count` if the re-read fails.
            let liveCount = count;
            try {
                liveCount = await getCount(currentAccount.companyId);
            } catch (countErr) {
                console.error(
                    '[SyncStateContext] Failed to re-read queue count after sync failure',
                    countErr
                );
            }
            // Preserve `unavailable` if the network dropped mid-sync instead
            // of unconditionally reporting `readyToSync` while offline.
            setStateIfMounted(
                isOnlineRef.current
                    ? readyToSync(liveCount)
                    : unavailable(liveCount)
            );
        } finally {
            isSyncingRef.current = false;
            setInFlightSync(null);
        }
    }

    // Recomputes and applies the live queue state (used by both the
    // punch-added listener and the `synced` reset timer) rather than each
    // caller guessing at a fixed target state.
    async function refreshLiveState(): Promise<void> {
        if (isSyncingRef.current) return;
        const currentAccount = accountRef.current;
        if (!currentAccount) return;

        const count = await getCount(currentAccount.companyId);
        if (isSyncingRef.current) return;

        if (!isOnlineRef.current) {
            setStateIfMounted(unavailable(count));
        } else {
            setStateIfMounted(count > 0 ? readyToSync(count) : nothingToSync());
        }
    }

    // Mount / account-becomes-available trigger. `account` resolves
    // asynchronously (AccountContext runs companies -> locations -> account
    // fetches, all *after* the access token is already set), so a plain
    // mount-only effect reliably fires while `account` is still null and
    // no-ops via the `runSync` guard. Nothing else is guaranteed to
    // re-trigger afterward — the token-changed trigger already fired its one
    // null->non-null transition before account resolved, and `isOnline`
    // may never toggle during a normal boot. Re-running whenever `account`
    // transitions from absent to present (in addition to an already-present
    // account at first mount) ensures queued punches from a prior session
    // still auto-sync on this boot.
    const hasAutoSyncedRef = useRef(false);
    useEffect(() => {
        if (!account || hasAutoSyncedRef.current) return;
        hasAutoSyncedRef.current = true;
        void runSync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account]);

    // Reconnect / went-offline / access-token-changed triggers.
    useEffect(() => {
        const wentOnline = !prevOnlineRef.current && isOnline;
        const wentOffline = prevOnlineRef.current && !isOnline;
        prevOnlineRef.current = isOnline;

        if (wentOnline) {
            void runSync();
            return;
        }

        if (wentOffline) {
            const currentAccount = accountRef.current;
            if (currentAccount) {
                void getCount(currentAccount.companyId).then(count => {
                    setStateIfMounted(unavailable(count));
                });
            } else {
                setStateIfMounted(unavailable(null));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnline]);

    useEffect(() => {
        const tokenChangedToFresh =
            prevTokenRef.current !== accessToken && accessToken != null;
        prevTokenRef.current = accessToken;

        if (tokenChangedToFresh && isOnlineRef.current) {
            void runSync();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    // Punch-added (live count) trigger — keeps the displayed count current
    // as punches accumulate offline, without polling.
    useEffect(() => {
        return subscribeQueueChanged(() => {
            void refreshLiveState();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <SyncStateContext.Provider value={state}>
            {children}
        </SyncStateContext.Provider>
    );
}

export function useSyncState(): SyncState {
    const ctx = useContext(SyncStateContext);
    if (!ctx) {
        throw new Error('useSyncState must be used within a SyncStateProvider');
    }
    return ctx;
}
